export const HENG_SPEC_DETAIL_METADATA_KEY = "hengSpecDetail";
export const PRODUCT_METADATA_TRANSLATIONS_KEY = "custom/product_metadata_translations";

export interface ProductMetadataItem {
	key?: string | null;
	value?: string | null;
}

export interface HengSpecDetailMediaItem {
	id?: string | number;
	file_id?: number;
	url?: string;
	name?: string;
	type?: string;
	alt?: string;
}

export interface HengSpecDetailItem {
	id?: string;
	title?: string;
	description?: string;
	media?: HengSpecDetailMediaItem[] | null;
	link?: string;
	isRequired?: boolean;
}

export interface HengSpecDetail {
	productInfo?: HengSpecDetailItem[];
	productRange?: HengSpecDetailItem[];
	usageScenarios?: HengSpecDetailItem[];
}

const parseJsonObject = (value: unknown): Record<string, unknown> | null => {
	if (typeof value === "string") {
		try {
			const parsedValue: unknown = JSON.parse(value);
			return parseJsonObject(parsedValue);
		} catch {
			return null;
		}
	}

	if (!value || typeof value !== "object" || Array.isArray(value)) return null;

	return value as Record<string, unknown>;
};

const hasHengSpecDetailContent = (value: Record<string, unknown>): boolean => {
	return [value.productInfo, value.productRange, value.usageScenarios].some(
		(section) => Array.isArray(section) && section.length > 0,
	);
};

const parseHengSpecDetail = (value: unknown): HengSpecDetail | null => {
	const parsedValue = parseJsonObject(value);
	if (!parsedValue || !hasHengSpecDetailContent(parsedValue)) return null;

	return parsedValue as HengSpecDetail;
};

const getMetadataValue = (
	metadata: readonly ProductMetadataItem[] | null | undefined,
	metadataKey: string,
): string | null => {
	const metadataItem = metadata?.find((item) => item.key === metadataKey);
	return typeof metadataItem?.value === "string" && metadataItem.value.trim()
		? metadataItem.value
		: null;
};

export const getLocalizedHengSpecDetail = (
	metadata: readonly ProductMetadataItem[] | null | undefined,
	locale: string,
): HengSpecDetail | null => {
	const originalHengSpecDetail = parseHengSpecDetail(
		getMetadataValue(metadata, HENG_SPEC_DETAIL_METADATA_KEY),
	);

	if (!locale || locale === "en") return originalHengSpecDetail;

	const translationMetadata = parseJsonObject(
		getMetadataValue(metadata, PRODUCT_METADATA_TRANSLATIONS_KEY),
	);
	const fields = parseJsonObject(translationMetadata?.fields);
	const hengSpecDetailTranslations = parseJsonObject(fields?.hengSpecDetail);
	const translationsByLocale = parseJsonObject(hengSpecDetailTranslations?.lang);
	const localizedHengSpecDetail = parseHengSpecDetail(translationsByLocale?.[locale]);

	return localizedHengSpecDetail || originalHengSpecDetail;
};
